import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { authAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { useTheme } from '../contexts/ThemeContext'
import TwoFactorVerify from '../components/TwoFactorVerify'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [loginError, setLoginError] = useState('')
  const [requires2FA, setRequires2FA] = useState(false)
  const [userId, setUserId] = useState<number | null>(null)
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)
  const { currentTheme } = useTheme()
  const logoSrc = currentTheme?.is_dark ? '/DashitLogo-white.png' : '/DashitLogo.png'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setLoginError('') // Clear previous error

    try {
      const { data } = await authAPI.login({ email, password })
      console.log('Login response:', data)

      // Check if 2FA is required
      if (data.requires_2fa) {
        setRequires2FA(true)
        setUserId(data.user_id)
        toast.success('Please enter your 2FA code')
        setLoading(false)
        return
      }

      // Save auth data
      setAuth(data.token, data.user)

      // Verify it was saved
      const saved = localStorage.getItem('token')
      console.log('Token saved to localStorage:', saved ? 'YES' : 'NO')

      // Wait a moment for state to update
      await new Promise(resolve => setTimeout(resolve, 100))

      toast.success('Welcome back!')

      // Redirect based on user role
      if (data.user.role === 'admin') {
        navigate('/admin')
      } else if (data.user.role === 'developer') {
        navigate('/developer')
      } else {
        navigate('/dashboard')
      }
    } catch (error: any) {
      console.error('Login error:', error)
      const errorMessage = error.response?.data?.error || 'Invalid email or password. Please try again.'
      setLoginError(errorMessage)
      toast.error(errorMessage, {
        duration: 5000, // Show for 5 seconds
        style: {
          background: '#ef4444',
          color: '#fff',
          fontWeight: 'bold'
        }
      })
      // Clear password field on failed login
      setPassword('')
    } finally {
      setLoading(false)
    }
  }
  
  const handleBack = () => {
    setRequires2FA(false)
    setUserId(null)
  }

  // Show 2FA verification if required
  if (requires2FA && userId) {
    return <TwoFactorVerify userId={userId} onBack={handleBack} />
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
        <div className="text-center mb-8">
          <img
            src={logoSrc}
            alt="Dashit"
            className="h-12 w-auto mx-auto mb-4"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const text = document.createElement('h1');
              text.textContent = 'Dashit';
              text.className = 'text-3xl font-bold text-gray-900 mb-2';
              e.currentTarget.parentElement?.insertBefore(text, e.currentTarget.nextElementSibling);
            }}
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashit</h1>
          <p className="text-gray-600">Sign in to your account</p>
        </div>

        {loginError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">
                  {loginError}
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (loginError) setLoginError('') // Clear error when user types
              }}
              required
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${
                loginError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                if (loginError) setLoginError('') // Clear error when user types
              }}
              required
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${
                loginError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary-600 font-semibold hover:text-primary-700">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

